// WO-35: listing preferences that follow the shopper from card to card (Pokemon, other games, sealed) and across
// visits: sort order, seller type, conditions and "near me" (on/off, radius, place). Kept only in this browser
// (localStorage); the location is rounded to ~1 km (as before it is sent to the API) and "Clear" removes it.
// Written only when the shopper changes a setting, never by a page pruning options a card does not have, so a card
// without store listings does not erase "stores only" for the next card.
import { useCallback, useState } from 'react';

export const PREFS_KEY = 'lc.listingPrefs.v1';
export const SORTS = ['Lowest Price', 'Highest Price', 'Most Recent', 'Nearest'];
export const NEAR_ME_RADII_KM = [10, 25, 50, 100, 250];
// A remembered browser location older than this is not used: the shopper is asked again.
export const NEAR_ME_MAX_AGE_MS = 30 * 24 * 3600 * 1000;

export const DEFAULT_NEAR_ME = { active: false, lat: null, lng: null, radiusKm: 50, label: '', origin: null };

const round = (v) => Math.round(Number(v) * 100) / 100;

const storage = () => {
  try {
    return typeof window !== 'undefined' && window.localStorage ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

// Keep only well-formed values; anything else falls back to the page's own default.
export function sanitizePrefs(raw, now = Date.now()) {
  const p = raw && typeof raw === 'object' ? raw : {};
  const out = {};
  if (SORTS.includes(p.sort)) out.sort = p.sort;
  if (Array.isArray(p.sellers)) out.sellers = p.sellers.filter((s) => typeof s === 'string').slice(0, 5);
  if (Array.isArray(p.conditions)) out.conditions = p.conditions.filter((s) => typeof s === 'string').slice(0, 10);
  const n = p.nearMe;
  if (n && typeof n === 'object') {
    const radiusKm = NEAR_ME_RADII_KM.includes(Number(n.radiusKm)) ? Number(n.radiusKm) : DEFAULT_NEAR_ME.radiusKm;
    const hasPoint = Number.isFinite(Number(n.lat)) && Number.isFinite(Number(n.lng)) && n.lat !== null && n.lng !== null
      && Math.abs(Number(n.lat)) <= 90 && Math.abs(Number(n.lng)) <= 180;
    const fresh = Number.isFinite(Number(n.savedAt)) && now - Number(n.savedAt) < NEAR_ME_MAX_AGE_MS;
    const usable = hasPoint && fresh;
    out.nearMe = {
      active: !!n.active && usable,
      // remembered "on" without a usable place: the page asks for the location again instead of guessing
      wanted: !!n.active,
      lat: usable ? round(n.lat) : null,
      lng: usable ? round(n.lng) : null,
      radiusKm,
      label: usable && typeof n.label === 'string' ? n.label.slice(0, 80) : '',
      origin: usable && ['profile', 'browser'].includes(n.origin) ? n.origin : null,
      savedAt: usable ? Number(n.savedAt) : null,
    };
  }
  return out;
}

export function loadPrefs(now = Date.now()) {
  const s = storage();
  if (!s) return {};
  try {
    return sanitizePrefs(JSON.parse(s.getItem(PREFS_KEY) || '{}'), now);
  } catch (e) {
    return {};
  }
}

export function savePrefs(patch, now = Date.now()) {
  const s = storage();
  const next = { ...loadPrefs(now), ...patch };
  if (patch.nearMe) {
    const n = patch.nearMe;
    next.nearMe = n.active || n.lat !== null
      ? { active: !!n.active, lat: n.lat === null ? null : round(n.lat), lng: n.lng === null ? null : round(n.lng),
        radiusKm: n.radiusKm, label: n.label || '', origin: n.origin || null,
        savedAt: n.lat === null ? null : (n.savedAt || now) }
      : { active: false, lat: null, lng: null, radiusKm: n.radiusKm, label: '', origin: null, savedAt: null };
  }
  if (s) {
    try {
      s.setItem(PREFS_KEY, JSON.stringify(next));
    } catch (e) { /* storage full or blocked: preferences just do not persist */ }
  }
  return sanitizePrefs(next, now);
}

// Initial near-me state of a page.
export function initialNearMe(prefs) {
  const n = prefs?.nearMe;
  if (!n) return { ...DEFAULT_NEAR_ME };
  return { active: n.active, lat: n.lat, lng: n.lng, radiusKm: n.radiusKm, label: n.label, origin: n.origin };
}

// The sort a page opens with: the remembered one when the page offers it ("Nearest" only while near me is on).
export function initialSort(prefs, pageDefault, options = SORTS) {
  const s = prefs?.sort;
  if (!s || !options.includes(s)) return pageDefault;
  if (s === 'Nearest' && !prefs?.nearMe?.active) return pageDefault;
  return s;
}

// Hook: near-me state that is remembered. `setNearMe(next)` updates the page and the stored preference.
export function useNearMePref() {
  const [prefs] = useState(() => loadPrefs());
  const [nearMe, setState] = useState(() => initialNearMe(prefs));
  const setNearMe = useCallback((next) => {
    const clean = { ...DEFAULT_NEAR_ME, ...next };
    setState(clean);
    savePrefs({ nearMe: clean });
  }, []);
  return [nearMe, setNearMe, !!prefs?.nearMe?.wanted && !prefs?.nearMe?.active];
}

// Hook: a remembered sort order (shared by every listing page).
export function useSortPref(pageDefault, options = SORTS) {
  const [sort, setState] = useState(() => initialSort(loadPrefs(), pageDefault, options));
  const setSort = useCallback((value, { remember = true } = {}) => {
    setState(value);
    if (remember) savePrefs({ sort: value });
  }, []);
  return [sort, setSort];
}

// Seller types: Pokemon pages label them 'eBay' / 'Local Game Store'; the product pages use the API's source.
export const SELLER_EBAY = 'eBay';
export const SELLER_STORE = 'Local Game Store';
export const sellerOfListing = (l) => (l?.source === 'ebay' ? SELLER_EBAY : SELLER_STORE);
